/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { deleteHandler, getHandler, postHandler, stdErrHandlerFactory } from "../../../utils/Requests";
import { AnyAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Blank, CreateUserRequest, DetailedUser, Effect, MutableUserInfo, Pagination, Setter } from "../../../utils/Interfaces";
import { AxiosResponse } from "axios";
import { setExistingUsers } from "./userManagementSlice";

export function refreshUsers(dispatch: Dispatch<AnyAction>, setLoading: Setter<boolean>) {
    getHandler(
        "/api/admin/users/list",
        dispatch,
        (response: AxiosResponse<Pagination<DetailedUser>, any>) => dispatch(setExistingUsers(response.data)),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function createUser(
    dispatch: Dispatch<AnyAction>,
    newUser: CreateUserRequest,
    setLoading: Setter<boolean>,
    onUserCreated: Effect
) {
    postHandler(
        "/api/admin/users/create",
        newUser,
        dispatch,
        (response) => {
            onUserCreated();
        },
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function modifyUserRole(
    roleKey: string,
    value: boolean,
    userId: number,
    dispatch: Dispatch<AnyAction>,
    onSuccess: Effect,
    setLoading: Setter<boolean>
) {
    postHandler(
        `/api/roles/${value ? "add" : "remove"}/${userId}/${roleKey}`,
        {},
        dispatch,
        (response) => {
            onSuccess();
        },
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function deleteUser(userId: number, dispatch: Dispatch<AnyAction>, onRemoveUser: Setter<number>) {
    deleteHandler<Blank>(
        `/api/admin/users/delete/${userId}`,
        dispatch,
        (response) => {
            onRemoveUser(userId);
        },
        stdErrHandlerFactory(dispatch),
        (isLoading) => {}
    );
}

export function disableUser(userId: number, dispatch: Dispatch<AnyAction>, onDisable: Effect) {
    modifyUserAccess(userId, "disable", dispatch, onDisable);
}

export function enableUser(userId: number, dispatch: Dispatch<AnyAction>, onEnable: Effect) {
    modifyUserAccess(userId, "enable", dispatch, onEnable);
}

function modifyUserAccess(userId: number, accessType: string, dispatch: Dispatch<AnyAction>, onUpdate: Effect) {
    postHandler(
        `/api/admin/users/${accessType}/${userId}`,
        {},
        dispatch,
        (response) => {
            onUpdate();
        },
        stdErrHandlerFactory(dispatch),
        (isLoading) => {}
    );
}

export function updateUserInfo(userId: number, updatedInfo: MutableUserInfo, dispatch: Dispatch<AnyAction>, onUpdated: Effect) {
    postHandler(
        `/api/admin/users/update-info/${userId}`,
        updatedInfo,
        dispatch,
        (response) => {
            onUpdated();
        },
        stdErrHandlerFactory(dispatch),
        (isLoading) => {}
    );
}

export function filterUsersByName(users: DetailedUser[], filter: string): DetailedUser[] {
    return users.filter(
        (user) =>
            (user.first_name + user.last_name)
                .replaceAll(" ", "")
                .toLowerCase()
                .includes(filter.replaceAll(" ", "").toLowerCase()) ||
            user.username.replaceAll(" ", "").toLowerCase().includes(filter.replaceAll(" ", "").toLowerCase())
    );
}
